import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import ListView from "../../component/ListView/ListView";
import {
  buildContentCard,
  contentStats,
} from "../../component/ListView/contentCard";
import { EpisodesData } from "./EpisodeData";
import { showToast } from "../../utils/Toastify/Toastify";
import { episodeDelete, episodeListApi, SeriesShowApi } from "../../api/HttpApis";
import { MdPlaylistAdd, MdOutlineContentCut } from "react-icons/md";
import { trimStr } from "../../utils/theme/ShortenedText/ShortenedText";
import { useSearch } from "../../context/SearchContext";
import { debounce } from "../../utils/debounce";
import { useRbac } from "../../rbac/RbacContext";
import ConvertDrawer from "./microDrama/ConvertDrawer";
import ConversionsPanel from "./microDrama/ConversionsPanel";
import { isMicroDramaFormat } from "./microDrama/microDramaService";
import lv from "../../component/ListView/ListView.module.css";
const Episodes = () => {
  const { seriesId, seasonId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const pathSegments = location.pathname.split("/");
  const endpoint = pathSegments[pathSegments.length - 1];
  const { name: episodes } = location.state || {};
  const [episodesData, setEpisodesData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedCreator, setSelectedCreator] = useState("");
  const [selectedRating, setSelectedRating] = useState("");
  const [selectedGenre, setSelectedGenre] = useState();
  const { searchQuery } = useSearch();
  const prevSearchQuery = useRef(searchQuery);
  const { can } = useRbac();
  // Micro Drama series get "Convert a video" (long video → vertical episodes).
  const [series, setSeries] = useState(null);
  const [converting, setConverting] = useState(false);
  const [conversionsKey, setConversionsKey] = useState(0);
  useEffect(() => {
    if (!seriesId) return undefined;
    let alive = true;
    SeriesShowApi(seriesId)
      .then((res) => alive && setSeries(res?.data?.data || null))
      .catch(() => alive && setSeries(null));
    return () => {
      alive = false;
    };
  }, [seriesId]);
  const isMicroDrama = isMicroDramaFormat(series?.series_format);
  const fetchData = useCallback(
    async (page, limit) => {
      try {
        if (!seriesId || !seasonId) return;
        const data = await EpisodesData(
          seriesId,
          seasonId,
          selectedDate,
          selectedCreator,
          selectedRating,
          selectedGenre,
          page,
          limit
        );
        setEpisodesData(data || []);
        // Always sync the total — an empty page (e.g. after deleting the last
        // item) must reset it to 0 rather than keep the stale count.
        setTotalCount(
          Array.isArray(data) && data.length > 0 ? data[0].totalCount || 0 : 0
        );
      } catch (err) {
        console.error("Error fetching episodes:", err);
        setEpisodesData([]);
      }
    },
    [seriesId, seasonId]
  );
  const handleDelete = async (id, metadata_id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this episode?"
    );
    if (!confirmed) return;
    // Use metadata_id if available, otherwise use id (same as edit)
    const deleteId = metadata_id || id;
    setDeletingId(deleteId);
    try {
      const response = await episodeDelete(deleteId);
      setEpisodesData((prev) => prev.filter((item) => (item.metadata_id || item.id) !== deleteId));
      // Keep the Total tile in step without a refetch.
      setTotalCount((c) => Math.max(0, (Number(c) || 0) - 1));
      showToast(response.data.message, "success");
    } catch (error) {
      showToast(
        error.response?.data?.message || "Failed to delete series.",
        "error"
      );
    } finally {
      setDeletingId(null);
      fetchData();
    }
  };
  const handleAddClick = () => {
    const existingEpisodeNumbers = episodesData.map((ep) => ep.episodeNumber);
    const maxEpisodeNumber = Math.max(0, ...existingEpisodeNumbers);
    const nextEpisodeNumber = maxEpisodeNumber + 1;
    navigate(`/video/${seriesId}/${seasonId}/${nextEpisodeNumber}`, {
      state: {
        name: "episodes",
        seriesId,
        seasonId,
        nextEpisodeNumber,
        episode: "episode",
      },
    });
  };
const handleBulkAddClick = () => {
  navigate(`/mainlayout/series/${seriesId}/seasons/${seasonId}/episodes/bulk`, {
    state: { seriesId, seasonId }
  });
};
  const handleEdit = (id, metadata_id) => {
    // Use metadata_id in URL if available, otherwise use original id
    const urlId = metadata_id || id;
    const hasMetadataId = Boolean(metadata_id);
    let path = `/video/${urlId}`;
    
    // Add series parameters as path segments
    const hasSeriesData = seriesId && seasonId;
    if (hasSeriesData) {
      path += `/${seriesId}/${seasonId}`;
    }
    
    // Add query parameter at the very end if no metadata_id
    if (!hasMetadataId) {
      path += `?meta=false`;
    }
    
    
    setEditingId(urlId);
    setTimeout(() => {
      navigate(path, {
        state: {
          seriesId,
          seasonId,
          name: endpoint,
          movieId: id, // Always pass original id for API calls
          metadata_id: metadata_id || "", // Include metadata_id in body parameters
          episode: "episode",
        },
      });
    }, 300);
  };
  const handleFilterChange = async (type, value) => {
    if (type === "genre") setSelectedGenre(value);
    if (type === "date") setSelectedDate(value);
    if (type === "creator") setSelectedCreator(value);
    if (type === "rating") setSelectedRating(value);
    const updatedGenre = type === "genre" ? value : selectedGenre;
    const updatedDate = type === "date" ? value : selectedDate;
    const updatedCreator = type === "creator" ? value : selectedCreator;
    const updatedRating = type === "rating" ? value : selectedRating;
    try {
      const response = await episodeListApi(
        seriesId,
        seasonId,
        "",
        updatedDate,
        updatedCreator,
        updatedRating,
        updatedGenre,
        searchQuery
      );
      const data = Array.isArray(response?.data?.data)
        ? response.data.data
        : [];
      const formattedData = data.map((item) => ({
        // Try different possible field names for the original ID
        id: item.episode_id || item.id || item.original_id || item.video_id, // Original ID for API calls
        metadata_id: item.metadata_id || item.meta_id || item.meta_data_id, // Metadata ID for URL
        name: trimStr(item.name),
        media: item.portrait,
        status: item.status,
        qc_status: item.qc_status,
        rating: item.rating,
        episodeNumber: item.episode_number,
        portrait_url: item.portrait,
        landscape_url: item.landscape,
        access: item.access,
        buttonText: "Edit",
        buttonText2: "Remove",
      }));
      setEpisodesData(formattedData);
      setTotalCount(response?.data?.total);
      showToast();
    } catch (error) {
      showToast("Failed to apply filter", "error");
    }
  };
  const debouncedFilterChange = useMemo(
    () => debounce(() => handleFilterChange(), 500),
    [selectedDate, selectedCreator, selectedRating, selectedGenre, searchQuery]
  );

  useEffect(() => {
    if (prevSearchQuery.current === searchQuery) {
      return;
    }
    prevSearchQuery.current = searchQuery;
    debouncedFilterChange();
  }, [searchQuery]);
  const refreshEpisodes = useCallback(() => {
    fetchData();
  }, [fetchData]);
  const cardFor = buildContentCard(handleEdit, handleDelete, deletingId);
  const stats = contentStats(episodesData, totalCount);

  const convertButton =
    isMicroDrama && can("series", "create") ? (
      <button type="button" className={lv.addBtn} onClick={() => setConverting(true)}>
        <MdOutlineContentCut />
        Convert a video
      </button>
    ) : null;

  return (
    <div>
      {isMicroDrama && (
        <ConversionsPanel
          seriesId={seriesId}
          seasonId={seasonId}
          refreshKey={conversionsKey}
          canEdit={can("series", "create")}
          onEpisodesChanged={refreshEpisodes}
        />
      )}
      <ListView
        heading="Episodes"
        data={episodesData}
        getCard={cardFor}
        stats={stats}
        button={true}
        handleFilterChange={handleFilterChange}
        selectedDate={selectedDate}
        selectedRating={selectedRating}
        totalCount={totalCount}
        onPageChange={fetchData}
        selectedGenre={selectedGenre}
        addLabel="Add Episodes"   
        onAdd={handleAddClick}
        addSecondaryLabel="Bulk Add"
        AddSecondaryIcon={MdPlaylistAdd}
        onAddSecondary={handleBulkAddClick}
        extraButton={convertButton}
      />
      {converting && (
        <ConvertDrawer
          seriesId={seriesId}
          seasonId={seasonId}
          seriesName={series?.name}
          onClose={() => setConverting(false)}
          onStarted={() => {
            setConverting(false);
            setConversionsKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
};
export default Episodes;